import { Button } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, HelpCircle, Printer } from "lucide-react";
import { TrainingTour } from "../tour/TrainingTour";
import { useTrainingTour } from "../tour/useTrainingTour";
import { sopPdfPreviewTourSteps } from "../tour/trainingTourSteps";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useSopDetailQuery } from "../api/queries";
import { trainingRoutes } from "../trainingRoutes";
import {
  TrainingSopPdfPreviewContent,
  type TrainingSopPdfModel,
} from "../ui/TrainingSopPdfPreviewContent";

export function SopPdfPreviewPage() {
  const tour = useTrainingTour("training-sop-pdf-preview-tour-completed");
  const { id } = useParams({ from: "/app/training/sop/$id/pdf-preview" });
  const navigate = useNavigate({ from: "/app/training/sop/$id/pdf-preview" });

  const { data: sop, isLoading, error } = useSopDetailQuery(id);

  const handleBack = () => {
    navigate({
      to: trainingRoutes.sopDetail,
      params: { id },
    });
  };

  const handlePrint = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <>
        <Header className="print:hidden" />
        <Main>
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-primary border-b-2" />
              <p className="mt-2 text-muted-foreground">ກຳລັງໂຫຼດ...</p>
            </div>
          </div>
        </Main>
      </>
    );
  }

  if (error || !sop) {
    return (
      <>
        <Header className="print:hidden" />
        <Main>
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <h2 className="mb-2 font-bold text-2xl text-destructive">
                ບໍ່ພົບ SOP
              </h2>
              <p className="mb-4 text-muted-foreground">
                SOP ທີ່ທ່ານຊອກຫາບໍ່ມີຢູ່ ຫຼື ທ່ານບໍ່ມີສິດເຂົ້າເຖິງ
              </p>
              <Button type="button" onClick={handleBack} className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                ກັບໄປທີ່ SOP
              </Button>
            </div>
          </div>
        </Main>
      </>
    );
  }

  const model = sop as TrainingSopPdfModel;

  return (
    <>
      <Header className="print:hidden" />

      <Main>
        <div
          className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between print:hidden"
          data-tourid="training-sop-pdf-toolbar"
        >
          <Button
            type="button"
            variant="ghost"
            onClick={handleBack}
            className="w-full gap-2 sm:w-auto"
          >
            <ArrowLeft className="h-4 w-4" />
            ກັບໄປທີ່ SOP
          </Button>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Button
              type="button"
              variant="outline"
              onClick={handlePrint}
              className="gap-2"
            >
              <Printer className="h-4 w-4" />
              ພິມ
            </Button>
          </div>
        </div>

        <div
          className="mb-4 flex items-center gap-2 print:hidden"
          data-tourid="training-sop-pdf-page-title"
        >
          <h1 className="font-bold text-2xl tracking-tight">{model.title}</h1>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8"
            onClick={tour.startTour}
            aria-label="ເລີ່ມທົດສອບການນຳທາງ"
            title="ເລີ່ມທົດສອບການນຳທາງ"
          >
            <HelpCircle className="size-4" />
          </Button>
        </div>

        <TrainingSopPdfPreviewContent sop={model} />

        <TrainingTour
          steps={sopPdfPreviewTourSteps}
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
      </Main>
    </>
  );
}
